/**
 * LeadStatusHistory — 25/02/2026
 *
 * Painel colapsável dentro do LeadDetail que mostra o audit trail
 * completo de mudanças de status (quem, quando, motivo, origem). Cada
 * lead tem `status_history: StatusHistoryEntry[]` embebido, com uma
 * entrada por transição.
 *
 * Origens (source) suportadas:
 *  - manual   : mudança feita pelo utilizador via UI (dropdown "Mudar Estado")
 *  - auto     : auto-transição do backend (ex: proposta gerada → proposta_pronta)
 *  - polling  : detectado via Microsoft Graph polling (RT cancelada/reagendada)
 *  - zoho     : sincronização Zoho CRM
 *  - system   : migração / seed / backfill
 *
 * Pedido do utilizador para audit trail ("so surprises like this become
 * traceable").
 */

import { useMemo, useState } from "react";
import { ChevronDown, ChevronUp, User, Sparkles, RefreshCw, Cable, ShieldCheck } from "lucide-react";
import { STATUS_LABELS } from "@/lib/api";

const SOURCE_META = {
  manual:  { icon: User,        label: "Manual",       tone: "text-primary" },
  auto:    { icon: Sparkles,    label: "Automático",   tone: "text-[hsl(var(--info))]" },
  polling: { icon: RefreshCw,   label: "Polling Outlook", tone: "text-[hsl(var(--warning))]" },
  zoho:    { icon: Cable,       label: "Zoho CRM",     tone: "text-purple-400" },
  system:  { icon: ShieldCheck, label: "Sistema",      tone: "text-muted-foreground" },
};

const fmtDateTime = (iso) => {
  try {
    if (!iso) return "—";
    const d = new Date(iso);
    return d.toLocaleString("pt-PT", {
      day: "2-digit", month: "2-digit", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
};

export default function LeadStatusHistory({ lead }) {
  const [open, setOpen] = useState(false);
  const history = useMemo(() => {
    const list = Array.isArray(lead?.status_history) ? [...lead.status_history] : [];
    // Ordem cronológica inversa (mais recente primeiro).
    list.sort((a, b) => String(b?.at || "").localeCompare(String(a?.at || "")));
    return list;
  }, [lead?.status_history]);

  if (!lead) return null;

  return (
    <div
      className="rounded-2xl border border-border bg-card overflow-hidden"
      data-testid="lead-status-history"
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between px-5 py-3 hover:bg-muted/40 transition"
        data-testid="lead-status-history-toggle"
      >
        <div className="flex items-center gap-2">
          <span className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-semibold">
            Histórico de Estados
          </span>
          <span className="text-[10px] text-muted-foreground/70">
            ({history.length} {history.length === 1 ? "registo" : "registos"})
          </span>
        </div>
        {open ? <ChevronUp className="w-4 h-4 text-muted-foreground" /> : <ChevronDown className="w-4 h-4 text-muted-foreground" />}
      </button>
      {open && (
        <div className="px-5 pb-5 pt-1 border-t border-border/50">
          {history.length === 0 ? (
            <div className="text-xs text-muted-foreground py-3">
              Sem mudanças de estado registadas ainda.
            </div>
          ) : (
            <ol className="relative border-l border-border/60 ml-2 pl-4 space-y-3 mt-3">
              {history.map((h, i) => {
                const meta = SOURCE_META[h.source] || SOURCE_META.system;
                const Icon = meta.icon;
                const fromLabel = h.from_status ? (STATUS_LABELS[h.from_status] || h.from_status) : null;
                const toLabel   = STATUS_LABELS[h.to_status] || h.to_status;
                return (
                  <li key={`${h.at}-${i}`} className="relative" data-testid={`status-history-entry-${i}`}>
                    <span
                      className={`absolute -left-[26px] top-1.5 flex items-center justify-center w-5 h-5 rounded-full bg-card border border-border ${meta.tone}`}
                      aria-hidden
                    >
                      <Icon className="w-3 h-3" />
                    </span>
                    <div className="text-sm text-foreground">
                      {fromLabel ? (
                        <>
                          <span className="text-muted-foreground">{fromLabel}</span>
                          <span className="text-muted-foreground mx-1.5">→</span>
                        </>
                      ) : (
                        <span className="text-muted-foreground text-[10px] uppercase tracking-wider mr-1.5">Início</span>
                      )}
                      <span className="font-semibold">{toLabel}</span>
                    </div>
                    <div className="text-[11px] text-muted-foreground mt-0.5 flex flex-wrap items-center gap-x-2">
                      <span>{fmtDateTime(h.at)}</span>
                      <span className="text-muted-foreground/40">·</span>
                      <span className={`${meta.tone} font-medium`}>{meta.label}</span>
                      {h.actor_name && (
                        <>
                          <span className="text-muted-foreground/40">·</span>
                          <span>por <span className="text-foreground">{h.actor_name}</span>{h.actor_role ? ` (${h.actor_role})` : ""}</span>
                        </>
                      )}
                    </div>
                    {h.reason && (
                      <div className="text-[11px] text-foreground/80 mt-1 italic pl-0.5">
                        “{h.reason}”
                      </div>
                    )}
                  </li>
                );
              })}
            </ol>
          )}
        </div>
      )}
    </div>
  );
}
